/**
 * useSupportSocket — WebSocket hook for a single support ticket conversation.
 *
 * Usage:
 *   const { connected, sendMessage } = useSupportSocket(reference, onEvent);
 *
 * `onEvent` is called with incoming frames:
 *   { type: 'message', id, author_name, author_user_id, body_html, is_staff_reply, created_at }
 *   { type: 'status_changed', status, updated_by }
 *   { type: 'pong' }
 *
 * Reconnects automatically with exponential back-off.
 * Socket is closed when reference becomes null/undefined.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const INITIAL_DELAY = 1_000;
const MAX_DELAY     = 30_000;

type SupportSocketEvent = Record<string, unknown>;

function getWsUrl(reference: string) {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/support/${reference}/`;
}

export function useSupportSocket(reference: string | null | undefined, onEvent: (event: SupportSocketEvent) => void) {
  const [connected, setConnected] = useState(false);
  const onEventRef   = useRef(onEvent);
  const wsRef        = useRef(null);
  const delayRef     = useRef(INITIAL_DELAY);
  const timerRef     = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  const open = useCallback(() => {
    if (unmountedRef.current || !reference) return;

    // The browser sends the HttpOnly session cookie with the WS upgrade
    // request automatically — no ?token= query parameter needed.
    const ws = new WebSocket(getWsUrl(reference));
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmountedRef.current) { ws.close(); return; }
      setConnected(true);
      delayRef.current = INITIAL_DELAY;
    };

    ws.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        onEventRef.current?.(payload);
      } catch (_) {/* ignore malformed frames */}
    };

    ws.onclose = (evt) => {
      setConnected(false);
      wsRef.current = null;
      if (unmountedRef.current) return;
      // 4403 = auth rejected; 4029 = rate limited — do not auto-reconnect
      if (evt.code === 4403 || evt.code === 4029) return;
      timerRef.current = setTimeout(() => {
        delayRef.current = Math.min(delayRef.current * 2, MAX_DELAY);
        open();
      }, delayRef.current);
    };

    ws.onerror = () => { ws.close(); };
  }, [reference]);

  useEffect(() => {
    unmountedRef.current = false;
    open();
    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [open]);

  const sendMessage = useCallback((bodyHtml: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', body_html: bodyHtml }));
    }
  }, []);

  return { connected, sendMessage };
}
